import { useEffect, useState } from 'react';

export const useIsScrolling: <T extends HTMLElement = HTMLDivElement>(
  trackScrollPosition?: boolean
) => [
  ((node: T | null) => void) & { current: T | null },
  { isVerticalScrolling: boolean; isHorizontalScrolling: boolean },
  { isAtTop: boolean; isAtBottom: boolean },
  { isAtLeft: boolean; isAtRight: boolean }
] = <T extends HTMLElement = HTMLDivElement>(trackScrollPosition?: boolean) => {
  // eslint-disable-next-line unicorn/no-null
  const [ref, setRef] = useState<T | null>(null);
  const asRef = setRef as ((node: T | null) => void) & { current: T | null };
  const [isVerticalScrolling, setIsVerticalScrolling] = useState(false);
  const [isHorizontalScrolling, setIsHorizontalScrolling] = useState(false);

  const getIsScrollAtTop = (isVerticalScrollingCurrent: boolean) => {
    if (!asRef.current) return false;

    if (!isVerticalScrollingCurrent) return true;

    return asRef.current.scrollTop === 0;
  };

  const getIsScrollAtBottom = (isVerticalScrollingCurrent: boolean) => {
    if (!asRef.current) return false;

    if (!isVerticalScrollingCurrent) return true;

    const { scrollTop, scrollHeight, clientHeight } = asRef.current;

    return scrollHeight - clientHeight - scrollTop <= 1;
  };

  const getIsScrollAtLeft = (isHorizontalScrollingCurrent: boolean) => {
    if (!asRef.current) return false;

    if (!isHorizontalScrollingCurrent) return true;

    return asRef.current.scrollLeft === 0;
  };

  const getIsScrollAtRight = (isHorizontalScrollingCurrent: boolean) => {
    if (!asRef.current) return false;

    if (!isHorizontalScrollingCurrent) return true;

    const { scrollLeft, scrollWidth, clientWidth } = asRef.current;

    return scrollWidth - clientWidth - scrollLeft <= 1;
  };

  const [isAtTop, setIsAtTop] = useState(getIsScrollAtTop(isVerticalScrolling));
  const [isAtBottom, setIsAtBottom] = useState(getIsScrollAtBottom(isVerticalScrolling));
  const [isAtLeft, setIsAtLeft] = useState(getIsScrollAtLeft(isHorizontalScrolling));
  const [isAtRight, setIsAtRight] = useState(getIsScrollAtRight(isHorizontalScrolling));

  useEffect(() => {
    if (!trackScrollPosition) return;

    /* istanbul ignore next */
    const handleScroll = () => {
      if (asRef.current && isVerticalScrolling) {
        setIsAtTop(getIsScrollAtTop(isVerticalScrolling));
        setIsAtBottom(getIsScrollAtBottom(isVerticalScrolling));
      }

      if (asRef.current && isHorizontalScrolling) {
        setIsAtLeft(getIsScrollAtLeft(isHorizontalScrolling));
        setIsAtRight(getIsScrollAtRight(isHorizontalScrolling));
      }
    };

    const tableContainer = asRef.current;

    /* istanbul ignore next */
    if (tableContainer && (isVerticalScrolling || isHorizontalScrolling)) {
      tableContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      /* istanbul ignore next */
      if (tableContainer && (isVerticalScrolling || isHorizontalScrolling)) {
        tableContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [asRef.current, isVerticalScrolling, isHorizontalScrolling]);

  useEffect(() => {
    if (!ref) return;

    setIsVerticalScrolling(ref.scrollHeight > ref.clientHeight);
    setIsHorizontalScrolling(ref.scrollWidth > ref.clientWidth);

    const resizeObserver = new ResizeObserver(() => {
      const isVerticalScrollingCurrent = ref.scrollHeight > ref.clientHeight;
      setIsVerticalScrolling(isVerticalScrollingCurrent);

      const isHorizontalScrollingCurrent = ref.scrollWidth > ref.clientWidth;
      setIsHorizontalScrolling(isHorizontalScrollingCurrent);

      setIsAtTop(getIsScrollAtTop(isVerticalScrollingCurrent));
      setIsAtBottom(getIsScrollAtBottom(isVerticalScrollingCurrent));
      setIsAtLeft(getIsScrollAtLeft(isHorizontalScrollingCurrent));
      setIsAtRight(getIsScrollAtRight(isHorizontalScrollingCurrent));
    });

    resizeObserver.observe(ref);

    return () => resizeObserver.disconnect();
  }, [ref]);

  useEffect(() => {
    asRef.current = ref;
  }, [ref]);

  return [asRef, { isVerticalScrolling, isHorizontalScrolling }, { isAtTop, isAtBottom }, { isAtLeft, isAtRight }];
};
